import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { colors, css, frames, glyphs, mediaQueriesNumbers, useMediaQuery } from '../../../../Styles'
import { BasicInfo, Events, Feed, Marking, PageHeader, Timeline, NewButtonList, Overlay } from '../../../../Components'
import { ButtonTypes } from '../../../../Definitions'
import { useLocation } from 'react-router'
import { timelineTypes } from '../../../../Components/Animals/timeline'
import { RootState, useAppSelector } from '../../../../Store'
import { Graph_QL, useFeedCurves } from '../../../../Services'
import { useAnimal } from '../../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal'
import { Pages } from '../../../../Navigation/root'
import { useAnimalOptions } from '../../../../Components/_Components'
import { AnimalPageAreas } from '../../../../Styles/frames'
import ConfirmModal from '../../../../Components/GlobalModals/confirmModal'
import { selectAnimalById } from '../../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/AnimalDataSlices/animalsSlice'
import { selectFeedCurveById } from '../../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/FeedDataSlices/feedCurvesSlice'
import { useDispatch, useSelector } from 'react-redux'
import { SET_EDIT_MODE } from '../../../../Store/UI/editModeSlice'

export enum ActiveEdits {
  basicData = "basicData",
  viewer = "viewer"
}

export default function Animal() {
  const {t} = useTranslation();
  let location = useLocation();
  const dispatchEditMode = useDispatch();
  const editMode = useSelector((state: RootState) => state.uiState.editMode.editMode);
  const editType = useSelector((state: RootState) => state.uiState.editMode.editType);

  // const [activeEdit, setActiveEdit] = useState<ActiveEdits | undefined>(undefined);

  const [selectedAnimal, setAnimal] = useState<number>(0)
  const [viewMode, setViewMode] = useState<"MARKING" | "FEED" | "EVENTS">("FEED")
  const [modalState, setModalState] = useState<"timeline" | "delete" | undefined>(undefined);
  
  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);
  
  const { call: getAnimalsInfo, data: animalsState, maintainEntries: updateAnimal } = useAnimal(connection);
  const { call: getFeedCurves} = useFeedCurves(connection);

  const animal = useAppSelector((state) => (selectAnimalById(state, selectedAnimal)));
  const feedCurve = useAppSelector((state) => (animal?.feedcurve_id ? selectFeedCurveById(state, animal?.feedcurve_id) : undefined)) 

  useEffect(() => {
    if (animal && animal.feedcurve_id && feedCurve === undefined) {
      getFeedCurves({ids: [animal.feedcurve_id]})
    }
  }, [animal, feedCurve, getFeedCurves])
  
  useEffect(() => {
    if (!(animalsState[domain])) {
      getAnimalsInfo({fetchAmount: 50}, connection);
    }
  },[animalsState, connection, domain, getAnimalsInfo])

  useEffect(() => {
    if (selectedAnimal && selectedAnimal !== 0) {
      getAnimalsInfo({ids:[selectedAnimal], ifFeedCurveBaseInfoOnly: false, ifSkipLastTenDaysConsumption: false}, connection)
    }
  }, [connection, getAnimalsInfo, selectedAnimal])

  // #region | Mediaqueries
  const timelineType = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`);
  const timelineQuery = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px), (max-height: ${mediaQueriesNumbers.ScreenHeightMax}px)`);
  const viewerMenuQuery = useMediaQuery(`(min-width: 500px) and (max-width: 690px), (min-width: 890px) and (max-width: 1000px), (min-width: 1200px)`);
  // #endregion

  const animalOptions = useAnimalOptions();
  
  // Safe initialization logic
  useEffect(() => {
    if (!selectedAnimal) {
      if (location.state?.params?.selected) {
        setAnimal(location.state.params.selected);
      } else if (animalOptions?.length) {
        setAnimal(animalOptions[0].id);
      }
    }
  }, [selectedAnimal, animalOptions, location.state?.params?.selected]);

  // #region | Viewer for Feed, Marking and Events
  let viewer: JSX.Element;

  switch (viewMode) {
    case "MARKING":
      viewer = <Marking selectedAnimal={selectedAnimal} />;
      break;
  
    case "EVENTS":
      viewer = <Events selectedAnimal={selectedAnimal} />;
      break;
  
    case "FEED":
    default:
      viewer = (
        <Feed
          onEditFeedView={() => dispatchEditMode(SET_EDIT_MODE({enabled: true, type: ActiveEdits.viewer}))}
          selectedAnimal={selectedAnimal}
          connect={{ connection, domain }}
        />
      );
      break;
  }
  // #endregion
  
  const currentIndex = animalOptions.findIndex(
    (animal) => animal.id === selectedAnimal
  );

  const handleNext = () => {
    if (!selectedAnimal || currentIndex === -1) return;
    const nextIndex = (currentIndex + 1) % animalOptions.length;
    const nextAnimalId = animalOptions[nextIndex].id;
    setAnimal(nextAnimalId);
  };
  
  const handlePrev = () => {
    if (!selectedAnimal || currentIndex === -1) return;
    const prevIndex = (currentIndex - 1 + animalOptions.length) % animalOptions.length;
    const prevAnimalId = animalOptions[prevIndex].id;
    setAnimal(prevAnimalId);
  };

  return (
    <frames.Page_inner>
      {editMode ? <Overlay beforeUnload onClickAlert /> : <></>}
      <PageHeader
        zIndex={99}
        topHeader={t(`menuHeaders.${Pages.animals}`, { defaultValue: "animals" })}
        optionButtons={{
          contents: timelineQuery
            ? [t("animalPages.animal.viewTimeline", { defaultValue: "View Timeline" }), t("animalPages.animal.deleteAnimal", { defaultValue: "delete animal" })]
            : [t("animalPages.animal.deleteAnimal", { defaultValue: "delete animal"})],
          onClick: (index) => {
            if (timelineQuery) {
              if (index === 0) { setModalState("timeline")}
              if (index === 1) { setModalState("delete")}
            } else {
              if (index === 0) { setModalState("delete") }
            }
          },
          disabled: animalOptions.length === 0 || !animalOptions || selectedAnimal === undefined || selectedAnimal === 0
        }}
        subSelect={{
          type: "config",
          onNext: handleNext,
          onPrev: handlePrev,
          options: animalOptions,
          defaultValue: animalOptions.find(animal => animal.id === selectedAnimal),
          onChange: (e) => { setAnimal(e?.id ?? location.state.params.selected) },
          disabled: animalOptions.length === 0 || !animalOptions
        }}
      />
      {animalOptions.length === 0 || !animalOptions ? <p style={{ ...css.emptyPen, color: colors.greyLightTertiary, textAlign: "center" }}>{t("animalPages.animals.emptyText", { defaultValue: "No animals found" })}</p>
        : selectedAnimal === undefined || selectedAnimal === 0 ? <p style={{ ...css.emptyPen, color: colors.greyLightTertiary, textAlign: "center" }}>{t("animalPages.animal.noAnimalSelected", { defaultValue: "No animal selected" })}</p>
        : (<>
        <frames.AnimalPage>
          { !timelineQuery && <div style={{gridArea: AnimalPageAreas.timeline}}><Timeline selectedAnimal={selectedAnimal} timelineType={timelineTypes.primary}/></div>}
          <div style={{ gridArea: AnimalPageAreas.basic_info, overflow: "hidden", zIndex: (editMode && editType === ActiveEdits.basicData) ? 9999 : undefined }}><BasicInfo onEditBasicData={() => dispatchEditMode(SET_EDIT_MODE({enabled: true, type: ActiveEdits.basicData}))} selectedAnimal={selectedAnimal}/></div>
            
            <div style={{ gridArea: AnimalPageAreas.view, overflow: "hidden", display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gridTemplateRows: "50px 1fr", height: "100%" }}>
        
            {/* Buttons */}
            <div style={{ gridArea: "1/1/2/2", padding: "0px 20px", zIndex: (!editMode) ? 999 : 0 }}>
              <NewButtonList
                contents={[
                  t("feed.general.feed", {defaultValue: "feed"}), 
                  t("animalPages.animal.marking", {defaultValue: "Marking"}), 
                  t("animalPages.animal.events", {defaultValue: "events"})
                ]} 
                onClick={(i => {
                  if (i === 0) { setViewMode("FEED")}
                  else if (i === 1) {setViewMode("MARKING")}
                  else if (i === 2) { setViewMode("EVENTS")}
                })}
                disabled={[false, true, false]}
                overrides={(index) => {
                  const selected = (viewMode === "FEED" && index === 0) || (viewMode === "MARKING" && index === 1) || (viewMode === "EVENTS" && index === 2)
                  return {
                    baseStyles: { borderRadius: viewerMenuQuery ? "8px 8px 0px 0px" : "8px", backgroundColor: viewerMenuQuery ? selected ? colors.greyPrimary : colors.greySecondary : !viewerMenuQuery&&selected ? colors.greyDarkSecondary : colors.transparent },
                    hoverStyles: { backgroundColor: viewerMenuQuery ? colors.greyPrimary : colors.greyDarkSecondary }
                  }
                }}
                buttonType={ButtonTypes.tableButton}
                startEnhancers={[
                  glyphs.FeedPlan({}), 
                  glyphs.Animal({}), 
                  glyphs.Calendar({})]}
                listOverrides={{
                  listGap: viewerMenuQuery ? "5px" : "10px"
                }}
                popOverrides={{
                  hasPop: viewerMenuQuery ? false : true,
                  popoverStyle: { backgroundColor: colors.greyDarkPrimary, padding: "10px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px"+colors.blackPrimary+"4d", right: "unset", top: "60px", left: "-10px"},
                  buttonType: ButtonTypes.tableButton,
                  openContent: viewMode === "FEED" ? "Feed" : viewMode === "MARKING" ? "Marking" : viewMode === "EVENTS" ? "Events" : undefined,
                  closeContent: "Close",
                  closeEnhancer: <div style={{}}>{glyphs.Close({})}</div>,
                  openEnhancer: viewMode === "FEED" ? <>{glyphs.FeedPlan({})}</> : viewMode === "MARKING" ? <>{glyphs.Animal({})}</> : viewMode === "EVENTS" ? <>{glyphs.Calendar({})}</> : undefined
                }}
              />
            </div>

            <div style={{gridArea: "1/1/3/3", zIndex: (editMode && editType === ActiveEdits.viewer) ? 9999 : 0}}>{viewer}</div>
          </div>
        </frames.AnimalPage>
      </>)}

      {modalState === "timeline" &&
        <Timeline selectedAnimal={selectedAnimal} timelineType={timelineType ? timelineTypes.verticalModal : timelineTypes.horizontalModal} isModalOpen={modalState === "timeline"} closeModal={() => setModalState(undefined)}/>
      }

      {modalState === "delete" && 
        <ConfirmModal
          disclaimer={t("animalPages.animal.deleteConfirm", { defaultValue: "Are you sure you want to delete this Animal?" })}
          onCancel={() => setModalState(undefined)}
          onConfirm={() => { setModalState(undefined); if (updateAnimal) { updateAnimal(undefined, { data: [{ animal_id: selectedAnimal, deleted: true, location: null, feedcurve_id: null, fk_feedcurve_milestones: null, milestone_day: null  }] }) } setAnimal(animalOptions[((animalOptions.findIndex(a => a.id === selectedAnimal))+1)%animalOptions.length].id)}}
        /> 
      }
    </frames.Page_inner>
  );
}